/**
 * This code is based on the Tiptap `CharacterCount` extension:
 * https://github.com/ueberdosis/tiptap/blob/main/packages/extensions/src/character-count/character-count.ts
 * It has been heavily modified to fit our needs.
 */
import crel from "crelt";
import type { Node } from "prosemirror-model";
import { type EditorState, Plugin, PluginKey } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { getOrCreateBottomControls } from "./toolbar";

import { t } from "i18n/i18n";

interface CharacterCountState {
  limit: number | null;
  display: "NONE" | "READ" | "EDIT";
}

export interface CharacterCountOptions {
  limit: number | null; // max character limit (null = no limit)
  enforce: boolean; // block input beyond the limit
  mode: "textSize" | "nodeSize"; // textSize = text characters only, nodeSize = text + ProseMirror positional tokens
  textCounter: (text: string) => number; // custom counter fn; defaults to text.length
  position: "before" | "after"; // placement of the count UI relative to the editor
  display: "NONE" | "READ" | "EDIT"; // NONE = hidden, READ = read-only, EDIT = shows limit controls
}

const prefix = "character-count";
export const key = new PluginKey<CharacterCountState>("characterCount");

export function countCharacters(node: Node, options: Partial<CharacterCountOptions> = {}): number {
  if (options.mode === "nodeSize") return node.nodeSize;
  const text = node.textBetween(0, node.content.size, "⏎", " ");
  return options.textCounter ? options.textCounter(text) : text.length;
}

class CharacterCountView {
  ui: HTMLDivElement;

  firstRun: boolean = true;

  constructor(
    readonly editorView: EditorView,
    readonly options: Partial<CharacterCountOptions> = {},
  ) {
    this.ui = crel("div", {
      class: `${prefix} ml-auto flex-none`,
    }) as HTMLDivElement;
    getOrCreateBottomControls(editorView.dom).appendChild(this.ui);

    this.update(editorView, editorView.state);
  }

  update(editorView: EditorView, prevState: EditorState) {
    const defaults = {
      limit: this.options?.limit || null,
      display: this.options?.display || "NONE",
    };
    const { limit, display } = key.getState(editorView.state) || defaults;
    const count = countCharacters(editorView.state.doc, this.options);

    const { limit: prevLimit, display: prevDisplay } = key.getState(prevState) || defaults;
    const prevCount = countCharacters(prevState.doc, this.options);

    this.ui.dataset.display = display;

    // Only update the UI if the count, limit, or display mode has changed
    if (!this.firstRun && count === prevCount && limit === prevLimit && display === prevDisplay) {
      return;
    }
    this.firstRun = false;

    const dom = editorView.dom;
    const limitReached = limit !== null && limit > 0 && count >= limit;
    const limitExceeded = limit !== null && limit > 0 && count > limit;

    // If we are *at* the limit, set a data attribute on the count UI
    if (limitReached) {
      this.ui.dataset.limitReached = "true";
    } else if (this.ui.dataset.limitReached) {
      delete this.ui.dataset.limitReached;
    }
    // If we have *exceeded* the limit, also set aria-invalid to true on the editor itself
    if (limitExceeded) {
      dom.setAttribute("aria-invalid", "true");
    } else if (dom.getAttribute("aria-invalid") === "true") {
      dom.removeAttribute("aria-invalid");
    }

    // Clear the UI and re-render it
    this.ui.innerHTML = "";
    this.ui.title = limitReached ? t("character_counter.reached_limit") : "";

    if ((display === "READ" || display === "EDIT") && limit) {
      this.ui.role = "status";
      this.ui.setAttribute("aria-live", "polite");
    } else {
      this.ui.removeAttribute("role");
      this.ui.removeAttribute("aria-live");
    }

    if ((display === "READ" || display === "EDIT") && limit) {
      this.ui.appendChild(
        crel(
          "div",
          {
            class:
              "cursor-default rounded border border-transparent p-1 text-xs leading-none " +
              "text-black-tint-55",
            "aria-hidden": "true",
          },
          [
            t("character_counter.characters_used_count", {
              textLength: count,
              maxLength: limit,
            }),
          ],
        ),
      );
      this.ui.appendChild(
        crel(
          "div",
          {
            class: "sr-only",
          },
          t("character_counter.screen_reader_status", {
            count,
            context: count >= limit ? "limited" : undefined,
            maxLength: limit,
          }),
        ),
      );
    }
  }

  destroy() {
    this.ui.remove();
  }
}

/** Creates a plugin that tracks and enforces character count limits. */
export function characterCountPlugin(options: Partial<CharacterCountOptions> = {}) {
  const { limit: initialLimit = null, display: initialDisplay = "NONE", enforce = false } = options;
  return new Plugin<CharacterCountState>({
    key,
    state: {
      init() {
        return { limit: initialLimit, display: initialDisplay };
      },
      apply(tr, value) {
        const meta = tr.getMeta(key) as Partial<CharacterCountState> | undefined;
        if (!meta) return value;
        let next = value;
        if (typeof meta.limit !== "undefined") next = { ...next, limit: meta.limit };
        if (typeof meta.display === "string") next = { ...next, display: meta.display };
        return next;
      },
    },
    view(editorView) {
      return new CharacterCountView(editorView, options);
    },
    filterTransaction(transaction, state) {
      if (!enforce) return true;

      const { limit } = key.getState(state) || { limit: initialLimit, display: initialDisplay };
      // Nothing has changed or no limit is defined. Ignore it.
      if (!transaction.docChanged || limit === 0 || limit === null || limit === undefined) {
        return true;
      }

      const oldSize = countCharacters(state.doc, options);
      const newSize = countCharacters(transaction.doc, options);

      // Everything is in the limit. Good.
      if (newSize <= limit) {
        return true;
      }

      // The limit has already been exceeded but will be reduced.
      if (oldSize > limit && newSize > limit && newSize <= oldSize) {
        return true;
      }

      // The limit has already been exceeded and will be increased further.
      if (oldSize > limit && newSize > limit && newSize > oldSize) {
        return false;
      }

      const isPaste = transaction.getMeta("paste");

      // Block all exceeding transactions that were not pasted.
      if (!isPaste) {
        return false;
      }

      // For pasted content, we try to remove the exceeding content.
      const pos = transaction.selection.$head.pos;
      const over = newSize - limit;
      const from = pos - over;
      const to = pos;

      // It’s probably a bad idea to mutate transactions within `filterTransaction`
      // but for now this is working fine.
      transaction.deleteRange(from, to);

      // In some situations, the limit will continue to be exceeded after trimming.
      // This happens e.g. when truncating within a complex node (e.g. table)
      // and ProseMirror has to close this node again.
      // If this is the case, we prevent the transaction completely.
      const updatedSize = countCharacters(transaction.doc, options);

      if (updatedSize > limit) {
        return false;
      }

      return true;
    },
  });
}
